/**
 * Iter 251 · HR Designations admin page.
 *
 * Two-pane layout: list of designations on the left, editable detail
 * on the right (name, department, job description rich text, default
 * duties table with frequency picker). Duty rows can be reordered
 * later — for now they're added/removed inline.
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, Save, Award, ClipboardList, GripVertical, Copy, ArrowUp, ArrowDown, Sparkles, Loader2, Wand2, ListPlus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SmartRichText from "@/components/SmartRichText";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AIDutyWizardDialog from "./AIDutyWizardDialog";
import { TemplatePropagationDialog } from "./DesignationPropagationDialog";
import { apiErrorMessage } from "@/lib/apiError";

const FREQUENCIES = [
  { value: "daily",       label: "Daily" },
  { value: "weekly",      label: "Weekly" },
  { value: "bi_weekly",   label: "Bi-Weekly" },
  { value: "monthly",     label: "Monthly" },
  { value: "bi_monthly",  label: "Bimonthly" },
  { value: "quarterly",   label: "Quarterly" },
  { value: "half_yearly", label: "Half Yearly" },
  { value: "yearly",      label: "Yearly" },
  { value: "one_time",    label: "One-time" },
];

export default function HRDesignations() {
  const { activeWorkspaceId } = useAuth();
  const [list, setList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sops, setSops] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [wizardCreateMode, setWizardCreateMode] = useState(false);
  const [jdWizardOpen, setJdWizardOpen] = useState(false);
  const [jdBusy, setJdBusy] = useState(false);
  const [jdFeedback, setJdFeedback] = useState("");
  const [jdIndustry, setJdIndustry] = useState("");
  const [jdPreview, setJdPreview] = useState("");
  const [jdVersionsOpen, setJdVersionsOpen] = useState(false);
  const [jdVersions, setJdVersions] = useState([]);
  const [jdVersionsLoading, setJdVersionsLoading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);
  // Iter Feb 2026 R19 · Propagation dialog state (opens after save when
  // the template's default_duties diff shows added/removed rows AND the
  // designation has at least one employee currently linked).
  const [propOpen, setPropOpen] = useState(false);
  const [propCtx, setPropCtx] = useState(null);   // { designationId, name, addedIds, removedIds }

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const [d, dept, sopList] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/hr/designations`),
        api.get(`/workspaces/${activeWorkspaceId}/departments`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/sops`).catch(() => ({ data: [] })),
      ]);
      setList(d.data || []);
      setDepartments(dept.data || []);
      const sopArr = Array.isArray(sopList.data) ? sopList.data : (sopList.data?.rows || []);
      setSops(sopArr);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load designations"));
    }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selected) setDraft(JSON.parse(JSON.stringify(selected)));
    else setDraft(null);
  }, [selected]);

  const newDesignation = () => {
    setSelected(null);
    setDraft({
      id: null,
      name: "",
      department: "",
      job_description: "",
      default_duties: [],
    });
  };

  const save = async () => {
    if (!draft?.name?.trim()) { toast.error("Name required"); return; }
    // Iter 252 · Warn if weightage doesn't total 100 (allow save, warn only)
    const totalW = (draft.default_duties || []).reduce((s, d) => s + (parseFloat(d.weightage) || 0), 0);
    if (totalW > 0 && Math.abs(totalW - 100) > 0.5) {
      if (!window.confirm(`Weightage total is ${totalW.toFixed(1)}% (should be 100%). Save anyway?`)) return;
    }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        department: draft.department || null,
        job_description: draft.job_description || null,
        default_duties: (draft.default_duties || []).filter((d) => d.title?.trim())
          .map(({ id, title, description, frequency, sop_id, weightage }, i) => ({
            // Preserve id when present so the backend can distinguish edits
            // from newly-added duties (used by the propagation dialog).
            ...(id ? { id } : {}),
            title: title.trim(),
            description: description || null,
            frequency: frequency || "monthly",
            sop_id: sop_id || null,
            weightage: parseFloat(weightage) || 0,
            order: i,
          })),
      };
      let data;
      // Iter Feb 2026 R19 · Compute added/removed duty template ids for
      // the propagation dialog. Only meaningful when patching an existing
      // designation (new designations obviously have no employees yet).
      //
      // Iter Feb 2026 R32 · Also compute EDITED template ids — rows whose
      // id survives but whose title / description / frequency / weightage
      // changed. These flow into the dialog's third radio option
      // ("push text edits to employees") — closing the R30 backend
      // capability that had no UI hook yet.
      const prevIds = new Set((selected?.default_duties || []).map((d) => d.id).filter(Boolean));
      const nextTitles = new Set(payload.default_duties.map((d) => d.title));
      const prevTitles = new Set((selected?.default_duties || []).map((d) => d.title));
      // Removed = ids present before but title now gone from the draft
      const removedIds = (selected?.default_duties || [])
        .filter((d) => d.id && !nextTitles.has(d.title))
        .map((d) => d.id);
      // R32 · Edited = same id, but at least one of title / description /
      // frequency / weightage / sop_id changed compared to the last-loaded row.
      const prevById = Object.fromEntries(
        (selected?.default_duties || []).filter((d) => d.id).map((d) => [d.id, d])
      );
      const editedIds = payload.default_duties
        .filter((d) => d.id && prevById[d.id])
        .filter((d) => {
          const p = prevById[d.id];
          return (
            (p.title || "") !== (d.title || "") ||
            (p.description || "") !== (d.description || "") ||
            (p.frequency || "monthly") !== (d.frequency || "monthly") ||
            (Number(p.weightage) || 0) !== (Number(d.weightage) || 0) ||
            (p.sop_id || "") !== (d.sop_id || "")
          );
        })
        .map((d) => d.id);

      if (draft.id) {
        const r = await api.patch(`/hr/designations/${draft.id}`, payload);
        data = r.data;
      } else {
        const r = await api.post(`/workspaces/${activeWorkspaceId}/hr/designations`, payload);
        data = r.data;
      }
      setSelected(data);
      // Now that the server assigned ids to newly-added rows, compute added ids
      const addedIds = (data.default_duties || [])
        .filter((d) => d.id && !prevIds.has(d.id) && !prevTitles.has(d.title))
        .map((d) => d.id);

      toast.success("Saved");
      load();

      if (draft.id && (addedIds.length || removedIds.length || editedIds.length)) {
        setPropCtx({
          designationId: data.id,
          designationName: data.name,
          addedIds, removedIds, editedIds,
        });
        setPropOpen(true);
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const cloneDesignation = async () => {
    if (!draft?.id) return;
    try {
      const { data } = await api.post(`/hr/designations/${draft.id}/clone`);
      toast.success(`Cloned as "${data.name}"`);
      setSelected(data);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Clone failed")); }
  };

  const del = async () => {
    if (!draft?.id) return;
    if (!window.confirm(`Delete designation "${draft.name}"? Employees on it will keep their existing duties.`)) return;
    try {
      await api.delete(`/hr/designations/${draft.id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const addDuty = () => setDraft((d) => ({
    ...d,
    default_duties: [...(d.default_duties || []), { title: "", description: "", frequency: "monthly", sop_id: null, weightage: 0 }],
  }));
  const patchDuty = (idx, patch) => setDraft((d) => ({
    ...d,
    default_duties: (d.default_duties || []).map((row, i) => i === idx ? { ...row, ...patch } : row),
  }));
  const removeDuty = (idx) => setDraft((d) => ({
    ...d,
    default_duties: (d.default_duties || []).filter((_, i) => i !== idx),
  }));
  const moveDuty = (idx, dir) => setDraft((d) => {
    const arr = [...(d.default_duties || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return d;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...d, default_duties: arr };
  });

  const totalWeightage = (draft?.default_duties || []).reduce((s, d) => s + (parseFloat(d.weightage) || 0), 0);
  const departmentOpts = [
    { value: "", label: "— No department —" },
    ...departments.map((d) => ({ value: d.name || d.slug || d.id, label: d.name || d.slug || d.id })),
  ];
  const sopOpts = [
    { value: "", label: "— No SOP —" },
    ...sops.map((s) => ({ value: s.id, label: s.title || s.name || s.id })),
  ];

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-4" data-testid="hr-designations-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">HR · Designations</h1>
            <p className="text-sm text-muted-foreground">Define seniority roles, their job descriptions, and default job duties inherited by every employee assigned to them.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={newDesignation}
                 className="flex items-center gap-1.5 px-3 py-2 border border-primary bg-primary text-primary-foreground text-sm hover:bg-primary/90"
                 data-testid="designation-new-btn">
            <Plus className="w-4 h-4" /> New designation
          </button>
          <button type="button" onClick={() => setBulkOpen(true)}
                 className="flex items-center gap-1.5 px-3 py-2 border border-border text-sm hover:bg-accent"
                 data-testid="designation-bulk-btn">
            <ListPlus className="w-4 h-4" /> Bulk add
          </button>
          <button type="button"
                 onClick={() => { setWizardCreateMode(true); setWizardOpen(true); }}
                 className="flex items-center gap-1.5 px-3 py-2 border border-purple-500 bg-purple-500 text-white text-sm hover:bg-purple-600"
                 data-testid="designation-ai-new-btn">
            <Sparkles className="w-4 h-4" /> New via AI
          </button>
        </div>
      </div>

      <div className="grid grid-cols-[280px_1fr] gap-4">
        <div className="border border-border" data-testid="designation-list">
          <div className="px-3 py-2 border-b border-border font-semibold text-sm bg-muted/30">All designations ({list.length})</div>
          {list.length === 0 && (
            <div className="p-4 text-xs text-muted-foreground text-center">No designations yet. Create your first one above.</div>
          )}
          {list.map((d) => (
            <button key={d.id} type="button"
                     onClick={() => setSelected(d)}
                     className={`w-full text-left px-3 py-2 border-b border-border last:border-b-0 hover:bg-muted/30 ${draft?.id === d.id ? "bg-primary/10" : ""}`}
                     data-testid={`designation-item-${d.id}`}>
              <div className="font-medium text-sm">{d.name}</div>
              {d.department && <div className="text-[11px] text-muted-foreground">{d.department}</div>}
              <div className="text-[10px] text-muted-foreground mt-0.5">{(d.default_duties || []).length} default duties</div>
            </button>
          ))}
        </div>

        {draft ? (
          <div className="border border-border p-4 space-y-4" data-testid="designation-editor">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="label-eyebrow mb-1">Name</div>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                       placeholder="e.g. Senior Software Engineer" data-testid="designation-name-input" />
              </div>
              <div>
                <div className="label-eyebrow mb-1">Department</div>
                <SearchableSelect
                  value={draft.department || ""}
                  onValueChange={(v) => setDraft({ ...draft, department: v || null })}
                  options={departmentOpts}
                  placeholder="Pick a department…"
                  triggerClassName="h-9 text-sm"
                  data-testid="designation-department-input"
                  width="100%"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="label-eyebrow">Job description</div>
                <div className="flex items-center gap-1">
                  {draft?.id && (
                    <button type="button"
                             onClick={async () => {
                               setJdVersionsOpen(true);
                               setJdVersionsLoading(true);
                               try {
                                 const { data } = await api.get(`/hr/designations/${draft.id}/jd-versions`);
                                 setJdVersions(data || []);
                               } catch (e) {
                                 toast.error(apiErrorMessage(e, "Failed to load history"));
                               } finally { setJdVersionsLoading(false); }
                             }}
                             className="text-[11px] px-2 py-1 border border-border hover:border-primary hover:text-primary flex items-center gap-1"
                             data-testid="designation-jd-history-btn">
                      <ClipboardList className="w-3 h-3" /> History
                    </button>
                  )}
                  <button type="button"
                           onClick={() => setJdWizardOpen(true)}
                           className="text-[11px] px-2 py-1 border border-purple-500 text-purple-600 hover:bg-purple-50 flex items-center gap-1"
                           data-testid="designation-jd-ai-btn">
                    <Sparkles className="w-3 h-3" /> {(draft.job_description || "").trim() ? "Refine with AI" : "Generate with AI"}
                  </button>
                </div>
              </div>
              <SmartRichText
                value={draft.job_description || ""}
                onChange={(html) => setDraft({ ...draft, job_description: html })}
                placeholder="Describe responsibilities, growth expectations, and role scope…"
                testid="designation-jd-input"
                minHeight={160}
                context={{ recordType: "designation", recordName: draft.name }}
              />
              <p className="text-[10px] text-muted-foreground mt-1">Rich text with slash commands + AI helpers. Rendered on employee profile & offer letters.</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> Default duties · Weightage total: <span className={`ml-1 ${Math.abs(totalWeightage - 100) < 0.5 ? "text-emerald-600" : "text-rose-600"} font-bold`} data-testid="designation-weightage-total">{totalWeightage.toFixed(1)}%</span></div>
                <div className="flex items-center gap-1.5">
                  {draft.id && (
                    <button type="button"
                             onClick={() => { setWizardCreateMode(false); setWizardOpen(true); }}
                             className="text-[11px] px-2 py-1 border border-purple-500 text-purple-600 hover:bg-purple-50 flex items-center gap-1"
                             data-testid="designation-ai-extend-btn">
                      <Sparkles className="w-3 h-3" /> AI suggest
                    </button>
                  )}
                  <button type="button" onClick={addDuty}
                           className="text-[11px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
                           data-testid="designation-add-duty-btn">
                    <Plus className="w-3 h-3" /> Add duty
                  </button>
                </div>
              </div>
              <div className="border border-border" data-testid="designation-duties-table">
                <div className="grid grid-cols-[60px_1fr_140px_140px_80px_60px] gap-2 px-2 py-1.5 bg-muted/30 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <div>Order</div>
                  <div>Duty</div>
                  <div>Frequency</div>
                  <div>SOP link</div>
                  <div>Weight %</div>
                  <div></div>
                </div>
                {(draft.default_duties || []).map((duty, i) => (
                  <div key={i}
                        draggable
                        onDragStart={(e) => {
                          setDragIdx(i);
                          e.dataTransfer.effectAllowed = "move";
                          try { e.dataTransfer.setData("text/plain", String(i)); } catch { /* Firefox */ }
                        }}
                        onDragOver={(e) => { e.preventDefault(); setDropIdx(i); }}
                        onDragLeave={() => setDropIdx((v) => (v === i ? null : v))}
                        onDragEnd={() => { setDragIdx(null); setDropIdx(null); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragIdx == null || dragIdx === i) { setDragIdx(null); setDropIdx(null); return; }
                          setDraft((d) => {
                            const list = [...(d.default_duties || [])];
                            const [moved] = list.splice(dragIdx, 1);
                            list.splice(i, 0, moved);
                            return { ...d, default_duties: list };
                          });
                          setDragIdx(null); setDropIdx(null);
                        }}
                        className={`grid grid-cols-[60px_1fr_140px_140px_80px_60px] gap-2 px-2 py-2 border-b border-border last:border-b-0 items-start transition-colors ${dragIdx === i ? "opacity-40" : ""} ${dropIdx === i && dragIdx !== i ? "bg-primary/5 border-t-2 border-t-primary" : ""}`}
                        data-testid={`designation-duty-row-${i}`}>
                    <div className="flex items-center gap-0.5 text-muted-foreground pt-1.5">
                      <button type="button" onClick={() => moveDuty(i, -1)} disabled={i === 0}
                               className="p-0.5 hover:text-primary disabled:opacity-30" title="Move up"
                               data-testid={`designation-duty-up-${i}`}>
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button type="button" onClick={() => moveDuty(i, 1)} disabled={i === (draft.default_duties || []).length - 1}
                               className="p-0.5 hover:text-primary disabled:opacity-30" title="Move down"
                               data-testid={`designation-duty-down-${i}`}>
                        <ArrowDown className="w-3 h-3" />
                      </button>
                      <GripVertical className="w-3 h-3 ml-1 cursor-grab active:cursor-grabbing" title="Drag to reorder" />
                    </div>
                    <div className="space-y-1">
                      <Input value={duty.title} onChange={(e) => patchDuty(i, { title: e.target.value })}
                             placeholder="Duty title…" className="h-8 text-sm"
                             data-testid={`designation-duty-title-${i}`} />
                      <SmartRichText
                        value={duty.description || ""}
                        onChange={(html) => patchDuty(i, { description: html })}
                        placeholder="Optional description…"
                        testid={`designation-duty-desc-${i}`}
                        minHeight={60}
                        context={{ recordType: "duty", recordName: duty.title }}
                      />
                    </div>
                    <Select value={duty.frequency} onValueChange={(v) => patchDuty(i, { frequency: v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`designation-duty-freq-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <SearchableSelect
                      value={duty.sop_id || ""}
                      onValueChange={(v) => patchDuty(i, { sop_id: v || null })}
                      options={sopOpts}
                      placeholder="Attach SOP…"
                      triggerClassName="h-8 text-xs"
                      data-testid={`designation-duty-sop-${i}`}
                      width="100%"
                    />
                    <Input type="number" min="0" max="100" step="0.5"
                            value={duty.weightage ?? 0}
                            onChange={(e) => patchDuty(i, { weightage: e.target.value })}
                            className="h-8 text-xs text-right"
                            data-testid={`designation-duty-weight-${i}`} />
                    <button type="button" onClick={() => removeDuty(i)}
                             className="text-muted-foreground hover:text-rose-600 p-1"
                             data-testid={`designation-duty-remove-${i}`}
                             title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                {(draft.default_duties || []).length === 0 && (
                  <div className="p-4 text-xs text-muted-foreground text-center">No default duties yet.</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <button type="button" onClick={save} disabled={saving}
                       className="flex items-center gap-1.5 px-3 py-2 border border-primary bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50"
                       data-testid="designation-save-btn">
                <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save designation"}
              </button>
              {draft.id && (
                <>
                  <button type="button" onClick={cloneDesignation}
                           className="flex items-center gap-1.5 px-3 py-2 border border-border hover:bg-muted text-sm"
                           data-testid="designation-clone-btn">
                    <Copy className="w-3.5 h-3.5" /> Clone
                  </button>
                  <button type="button" onClick={del}
                           className="flex items-center gap-1.5 px-3 py-2 border border-border hover:border-rose-500 hover:text-rose-600 text-sm"
                           data-testid="designation-delete-btn">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
            Select a designation on the left, or click <b>New designation</b> to create one.
          </div>
        )}
      </div>

      <AIDutyWizardDialog
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        designationId={wizardCreateMode ? null : (draft?.id || null)}
        seedName={wizardCreateMode ? "" : (draft?.name || "")}
        seedDepartment={wizardCreateMode ? "" : (draft?.department || "")}
        existingDutiesCount={wizardCreateMode ? 0 : (draft?.default_duties?.length || 0)}
        onApplied={async (des) => {
          await load();
          setSelected(des);
        }}
      />

      <Dialog open={jdWizardOpen} onOpenChange={(o) => { setJdWizardOpen(o); if (!o) { setJdFeedback(""); setJdIndustry(""); setJdPreview(""); } }}>
        <DialogContent className="max-w-2xl" data-testid="jd-wizard-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              {(draft?.job_description || "").trim() ? "Refine Job Description" : "Generate Job Description"}
            </DialogTitle>
            <DialogDescription>
              {(draft?.job_description || "").trim()
                ? "Type what you'd like to change (tone, length, focus), and Claude will revise the current JD."
                : "Claude will generate a polished JD based on the designation name, department, and any extra context you provide."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="label-eyebrow mb-1">Designation</div>
                <Input value={draft?.name || ""} readOnly className="bg-muted/30" data-testid="jd-wizard-name" />
              </div>
              <div>
                <div className="label-eyebrow mb-1">Industry (context, optional)</div>
                <Input value={jdIndustry} onChange={(e) => setJdIndustry(e.target.value)}
                          placeholder="e.g. SaaS, Fintech, Ad agency"
                          data-testid="jd-wizard-industry" />
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">
                {(draft?.job_description || "").trim() ? "Refinement prompt" : "Extra guidance (optional)"}
              </div>
              <Textarea value={jdFeedback} onChange={(e) => setJdFeedback(e.target.value)}
                            rows={3}
                            placeholder={(draft?.job_description || "").trim()
                              ? "e.g. 'Make it more concise', 'Emphasise remote-first', 'Add a Growth section'"
                              : "e.g. 'Reports into VP of Product, owns the design system, 5 direct reports'"}
                            data-testid="jd-wizard-feedback" />
            </div>
            {jdPreview && (
              <div className="border border-border p-3 space-y-2 max-h-64 overflow-y-auto bg-muted/10">
                <div className="label-eyebrow">Preview</div>
                <div className="prose prose-sm max-w-none text-sm"
                      dangerouslySetInnerHTML={{ __html: jdPreview }}
                      data-testid="jd-wizard-preview" />
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setJdWizardOpen(false); setJdPreview(""); }}
                       className="px-3 py-2 border border-border hover:bg-muted text-sm"
                       data-testid="jd-wizard-cancel">
                Cancel
              </button>
              <button type="button" onClick={async () => {
                if (!draft?.name?.trim()) { toast.error("Save the designation name first"); return; }
                setJdBusy(true);
                try {
                  const { data } = await api.post(
                    `/workspaces/${activeWorkspaceId}/hr/designations/jd-wizard`,
                    {
                      designation_name: draft.name.trim(),
                      department: draft.department || null,
                      industry: jdIndustry || null,
                      current_jd: draft.job_description || null,
                      feedback: jdFeedback || null,
                    },
                  );
                  setJdPreview(data.job_description || "");
                  if (!data.job_description) toast.warning("Claude returned an empty JD — try again with more guidance.");
                } catch (e) {
                  toast.error(apiErrorMessage(e, "Failed"));
                } finally { setJdBusy(false); }
              }} disabled={jdBusy}
                       className="px-3 py-2 border border-primary text-primary hover:bg-primary/10 text-sm disabled:opacity-50 flex items-center gap-1.5"
                       data-testid="jd-wizard-generate">
                {jdBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                {jdBusy ? "Thinking…" : (jdPreview ? "Regenerate" : "Generate")}
              </button>
              {jdPreview && (
                <button type="button" onClick={() => {
                  setDraft({ ...draft, job_description: jdPreview });
                  setJdWizardOpen(false); setJdPreview(""); setJdFeedback(""); setJdIndustry("");
                  toast.success("Applied — remember to save the designation");
                }}
                         className="px-3 py-2 border border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600 text-sm"
                         data-testid="jd-wizard-apply">
                  Apply to JD
                </button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={jdVersionsOpen} onOpenChange={setJdVersionsOpen}>
        <DialogContent className="max-w-3xl" data-testid="jd-versions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              Job Description version history
            </DialogTitle>
            <DialogDescription>
              Snapshots taken every time the JD was saved with new text. Click <b>Restore</b> to bring an older version back — the current text will be snapshotted first.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {jdVersionsLoading && <div className="text-xs text-muted-foreground">Loading…</div>}
            {!jdVersionsLoading && jdVersions.length === 0 && (
              <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground" data-testid="jd-versions-empty">
                No previous versions yet. History fills up as you edit the JD.
              </div>
            )}
            {jdVersions.map((v) => (
              <div key={v.id} className="border border-border p-3 space-y-2" data-testid={`jd-version-${v.id}`}>
                <div className="flex items-center justify-between text-[11px]">
                  <div className="text-muted-foreground">
                    Replaced <b>{v.replaced_at?.slice(0, 16)}</b> by {v.replaced_by_name || "system"}
                    {v.restored_from && <span className="italic"> · restored earlier</span>}
                  </div>
                  <button type="button"
                           onClick={async () => {
                             if (!window.confirm("Restore this version? The current JD will be moved to history.")) return;
                             try {
                               const { data } = await api.post(`/hr/designations/${draft.id}/jd-versions/${v.id}/restore`);
                               setDraft(data);
                               setSelected(data);
                               const { data: vs } = await api.get(`/hr/designations/${draft.id}/jd-versions`);
                               setJdVersions(vs || []);
                               toast.success("Version restored");
                             } catch (e) {
                               toast.error(apiErrorMessage(e, "Restore failed"));
                             }
                           }}
                           className="text-[10px] px-2 py-0.5 border border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600"
                           data-testid={`jd-version-${v.id}-restore`}>
                    Restore
                  </button>
                </div>
                <div className="prose prose-sm max-w-none text-xs border border-border bg-muted/10 p-2 max-h-40 overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: v.job_description || "" }} />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {propOpen && propCtx && (
        <TemplatePropagationDialog
          open={propOpen}
          onClose={() => { setPropOpen(false); setPropCtx(null); }}
          designationId={propCtx.designationId}
          designationName={propCtx.designationName}
          addedTemplateIds={propCtx.addedIds}
          removedTemplateIds={propCtx.removedIds}
          editedTemplateIds={propCtx.editedIds}
          onApplied={() => { setPropOpen(false); setPropCtx(null); load(); }}
        />
      )}
      {bulkOpen && (
        <DesignationBulkAddDialog
          wsId={activeWorkspaceId}
          existingNames={list.map((d) => d.name)}
          onClose={() => setBulkOpen(false)}
          onSaved={async () => { setBulkOpen(false); await load(); }}
        />
      )}
    </div>
  );
}

// Feb 2026 · Bulk-add designations by pasting names one per line.
// Mirrors DeptBulkAddDialog on the Departments page — same preview
// split (Will add · Already exists · Duplicate in paste) so admins can
// tune the paste before committing.
function DesignationBulkAddDialog({ wsId, existingNames, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const parsed = (() => {
    const seen = new Set();
    const willAdd = [], dupInBatch = [], dupExisting = [];
    const existingLc = new Set((existingNames || []).map((n) => (n || "").trim().toLowerCase()));
    for (const raw of (text || "").split(/\r?\n/)) {
      const n = raw.trim();
      if (!n) continue;
      const lc = n.toLowerCase();
      if (seen.has(lc)) { dupInBatch.push(n); continue; }
      seen.add(lc);
      if (existingLc.has(lc)) dupExisting.push(n);
      else willAdd.push(n);
    }
    return { willAdd, dupInBatch, dupExisting };
  })();
  const submit = async () => {
    if (parsed.willAdd.length === 0) { toast.error("Nothing new to add"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/hr/designations/bulk`, { names: parsed.willAdd });
      toast.success(`Added ${data.created_count} designation${data.created_count === 1 ? "" : "s"}${data.skipped_count ? ` · ${data.skipped_count} skipped` : ""}`);
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk add failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="designation-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListPlus className="w-4 h-4" /> Bulk add designations
          </DialogTitle>
          <DialogDescription className="text-xs">
            Paste one name per line. Case-insensitive de-dupe against existing designations.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block mb-1.5 text-sm font-semibold">Paste designation names — one per line</label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={10}
                        placeholder={"Senior Designer\nJunior Developer\nAccount Manager\nProject Lead\n…"}
                        className="font-mono text-sm"
                        data-testid="designation-bulk-textarea" />
            <div className="mt-1 text-[11px] text-muted-foreground">Up to 200 names. Empty lines / duplicates ignored. Job description &amp; duties can be added later.</div>
          </div>
          {(parsed.willAdd.length + parsed.dupExisting.length + parsed.dupInBatch.length) > 0 && (
            <div className="border border-border text-xs grid grid-cols-3 divide-x divide-border"
                 data-testid="designation-bulk-preview">
              <div className="p-3">
                <div className="label-eyebrow text-[10px] text-emerald-700 font-semibold mb-1.5">Will add · {parsed.willAdd.length}</div>
                <ul className="space-y-0.5 max-h-40 overflow-auto">
                  {parsed.willAdd.length === 0 ? <li className="text-muted-foreground italic">Nothing yet</li>
                    : parsed.willAdd.map((n, i) => <li key={i} className="font-mono truncate" title={n} data-testid={`designation-bulk-willadd-${i}`}>{n}</li>)}
                </ul>
              </div>
              <div className="p-3">
                <div className="label-eyebrow text-[10px] text-amber-700 font-semibold mb-1.5">Already exists · {parsed.dupExisting.length}</div>
                <ul className="space-y-0.5 max-h-40 overflow-auto">
                  {parsed.dupExisting.length === 0 ? <li className="text-muted-foreground italic">None</li>
                    : parsed.dupExisting.map((n, i) => <li key={i} className="font-mono truncate text-muted-foreground" title={n}>{n}</li>)}
                </ul>
              </div>
              <div className="p-3">
                <div className="label-eyebrow text-[10px] text-rose-700 font-semibold mb-1.5">Duplicate in paste · {parsed.dupInBatch.length}</div>
                <ul className="space-y-0.5 max-h-40 overflow-auto">
                  {parsed.dupInBatch.length === 0 ? <li className="text-muted-foreground italic">None</li>
                    : parsed.dupInBatch.map((n, i) => <li key={i} className="font-mono truncate text-muted-foreground" title={n}>{n}</li>)}
                </ul>
              </div>
            </div>
          )}
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} disabled={busy}
                      className="px-4 py-2 border border-border text-sm hover:bg-accent">Cancel</button>
            <button type="button" onClick={submit} disabled={busy || parsed.willAdd.length === 0}
                      className="px-4 py-2 bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50"
                      data-testid="designation-bulk-save">
              {busy ? "Adding…" : `Add ${parsed.willAdd.length}`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
